import { ViewConversationEntry } from '../../api/dealer-api-interface-franchisee';
import { customElement, property, state } from 'lit/decorators.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { LitElementBase } from '../../../webmodule-common/other/litelement-base';
import { LangBranchQuoteConversation } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-conversation-lang';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';

// Deprecated original tag retained for backward compatibility. New preferred tag: <wm-conversation-view>
@customElement('wm-conversation-view')
export class ConversationView extends LitElementBase {
  @property({ type: String }) supplierName = '';
  @property({ type: Number }) initialDisplayLead = 1;
  @property({ type: Number }) initialDisplayTail = 3;
  @property({ type: Boolean }) attachmentsMode = false;
  @property({ type: Boolean }) sortAscending = false;
  @property({ type: String }) emptyNote = LangBranchQuoteConversation.noEntriesVisible();
  @state() displayCount = 4;
  @property({ attribute: false })
  private _entries: ViewConversationEntry[] = [];
  public get entries(): ViewConversationEntry[] {
    return this._entries;
  }
  public set entries(value: ViewConversationEntry[]) {
    this._entries = value;
    if (value.length === 0) {
      console.debug('ConversationView: entries set to empty array');
    }
  }

  private onRequestEdit = (e: CustomEvent<{ entry: ViewConversationEntry }>) => {
    e.stopPropagation();
    this.dispatchCustom('request-edit-entry', { entry: e.detail.entry });
  };

  render() {
    return html`<div class="conversation">${this.conversationTemplates()}</div>`;
  }

  hasDownloads(entry: ViewConversationEntry) {
    return entry.attachments.some(x => !x.embedded);
  }

  conversationTemplates(): unknown {
    const userId = getCurrentUser()?.id;

    const data = this.entries;
    const entries = this.attachmentsMode
      ? [
          ...data.filter(item => !item.draft && this.hasDownloads(item)),
          ...data.filter(item => item.draft && item.systemUserId === userId && this.hasDownloads(item))
        ]
      : [...data.filter(item => !item.draft), ...data.filter(item => item.draft && item.systemUserId === userId)];

    const sortDirection = this.sortAscending ? -1 : 1;
    entries.sort((a: ViewConversationEntry, b: ViewConversationEntry) => {
      if (a.dateCreated === b.dateCreated) return 0;

      const order = a.dateCreated < b.dateCreated ? -1 : 1;
      return order * sortDirection;
    });

    const results: any[] = [];
    const repeatRange = (start: number, end?: number) => {
      return repeat(
        entries.slice(start, end),
        entry => entry.id,
        entry => {
          const classes = `conversation-messages ${entry.draft ? 'draft' : ''} `;
          return html`
            <div class=${classes}>
              <wmview-conversation-entry-view
                .attachmentsOnly=${this.attachmentsMode}
                .readonly=${this.readonly}
                @wm-event-request-edit=${this.onRequestEdit}
                .conversationEntry=${entry}
              ></wmview-conversation-entry-view>
            </div>
          `;
        }
      );
    };

    if (entries.length === 0) return html`<h1 class="text-muted">${this.emptyNote}</h1>`;
    if (
      this.attachmentsMode ||
      this.initialDisplayLead + this.initialDisplayTail >= entries.length ||
      this.displayCount >= entries.length
    )
      return repeatRange(0);
    const leading = this.displayCount - this.initialDisplayTail;
    results.push(repeatRange(0, leading));
    if (this.displayCount < entries.length) {
      results.push(this.showMore(entries.length));
      results.push(repeatRange(entries.length - this.initialDisplayTail));
    }
    return results;
  }

  showMore(count: number): any {
    const showSize = 3;
    const available = count - this.displayCount;
    const showMore = Math.min(showSize, available);
    const eventShowMore = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.displayCount = Math.min(count, this.displayCount + showMore);
    };
    const eventShowAll = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.displayCount = count;
    };
    if (showMore <= 0) return html``;

    const showAll =
      available > showSize
        ? html`<a class="ms-5" href="#" @click=${eventShowAll}
            >${LangBranchQuoteConversation.showAllMessages({ count })}</a
          >`
        : html``;
    return html`
      <div class="row">
        <div class="col d-flex justify-content-center border-top border-bottom border-secondary p-3">
          <a href="#" @click=${eventShowMore}>${LangBranchQuoteConversation.showMoreMessages({ count: showMore })}</a
          >${showAll}
        </div>
      </div>
    `;
  }
}
